/**
 * CreditTopUpModal — the universal owner "buy outcome" popup.
 *
 * Opened when a gateway call returns 402 `insufficient_credits`.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FmBanner, FmButton, FmModal, FmStack, FmText, FmUnlockCard, useFmToast,
} from '@/components/ui';
import { CreditsAPI, type FlatSku } from '@/api/credits';
import type { InsufficientCreditsDetail } from '@/api/client';
import { getFeatureFlag } from '@/config/featureFlags';

import { appRoutePatterns } from '@/shared/navigation/appRoutePatterns';
interface CreditTopUpModalProps {
  open: boolean;
  detail: InsufficientCreditsDetail | null;
  onClose: () => void;
}

const FLAT_SKU_DISABLED_CODE = 'flat_sku_billing_disabled';
const FLAT_SKU_DISABLED_MESSAGE = 'Flat-SKU checkout is not enabled.';

function isFlatSkuBillingDisabled(error: unknown): boolean {
  const response = (error as { response?: { status?: number; data?: unknown } }).response;
  if (response?.status !== 503) return false;

  const data = response.data as {
    code?: string;
    error_code?: string;
    detail?: string | { code?: string; error_code?: string; detail?: string };
  } | undefined;
  const nestedDetail = typeof data?.detail === 'object' ? data.detail : undefined;
  const detailText = typeof data?.detail === 'string' ? data.detail : nestedDetail?.detail;

  const codes = [data?.error_code, data?.code, nestedDetail?.error_code, nestedDetail?.code];
  return codes.includes(FLAT_SKU_DISABLED_CODE) || detailText === FLAT_SKU_DISABLED_MESSAGE;
}

function formatPrice(sku: FlatSku): string {
  const amount = sku.price.unit_amount / 100;
  const formatted = new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: sku.price.currency.toUpperCase(),
    maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
  }).format(amount);
  return sku.price.interval ? `${formatted}/${sku.price.interval}` : formatted;
}

function extractPropertyId(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  const match = window.location.pathname.match(
    /\/app\/(?:operations-planning|forest-overview|stand-detail|measurements)\/([^/]+)/
  );
  return match?.[1] ? decodeURIComponent(match[1]) : undefined;
}

interface SkusSectionProps {
  skus: FlatSku[];
  buyingId: string | null;
  loadingSkus: boolean;
  onBuy: (sku: FlatSku) => void;
}

function SkusSection(props: SkusSectionProps): React.ReactElement {
  return (
    <>
      <FmText as="span" tone="muted">Buy the outcome that fits this property:</FmText>
      <FmStack gap="sm">
        {props.skus.map((sku) => (
          <FmUnlockCard
            key={sku.sku_key}
            data-testid={`credit-top-up-sku-${sku.sku_key}`}
            title={sku.display_label}
            priceLabel={formatPrice(sku)}
            teaser={sku.description || undefined}
            ctaLabel="Buy"
            ctaTestId={`credit-top-up-sku-${sku.sku_key}-buy`}
            loading={props.buyingId === sku.sku_key}
            disabled={props.loadingSkus || (props.buyingId !== null && props.buyingId !== sku.sku_key)}
            onCtaClick={() => props.onBuy(sku)}
          />
        ))}
      </FmStack>
      <FmText as="span" tone="muted" size="fine">
        Checkout is handled securely by Stripe.
      </FmText>
    </>
  );
}

const CreditTopUpModal: React.FC<CreditTopUpModalProps> = ({
  open,
  detail,
  onClose,
}) => {
  const toast = useFmToast();
  const navigate = useNavigate();
  const [buyingId, setBuyingId] = useState<string | null>(null);
  const [skus, setSkus] = useState<FlatSku[]>([]);
  const [loadingSkus, setLoadingSkus] = useState(false);
  const catalogRequestGeneration = useRef(0);
  const inferredPropertyId = useMemo(
    () => detail?.propertyId ?? extractPropertyId(),
    [detail?.propertyId],
  );

  useEffect(() => {
    const generation = ++catalogRequestGeneration.current;
    if (!open) {
      setSkus([]);
      setLoadingSkus(false);
      return;
    }
    if (!getFeatureFlag('flat_sku_billing')) {
      setSkus([]);
      setLoadingSkus(false);
      return;
    }
    setLoadingSkus(true);
    CreditsAPI.getFlatSkus()
      .then((catalog) => {
        if (catalogRequestGeneration.current !== generation) return;
        if (!catalog.available) {
          setSkus([]);
          return;
        }
        setSkus(catalog.skus);
      })
      .catch((error) => {
        if (catalogRequestGeneration.current !== generation) return;
        if (isFlatSkuBillingDisabled(error)) {
          setSkus([]);
        }
      })
      .finally(() => {
        if (catalogRequestGeneration.current === generation) {
          setLoadingSkus(false);
        }
      });
  }, [open]);

  const handleBuy = async (sku: FlatSku) => {
    if (sku.grant_type !== 'none' && !inferredPropertyId) {
      toast.error('Open this purchase from the property you want to unlock.');
      return;
    }
    setBuyingId(sku.sku_key);
    try {
      const session = await CreditsAPI.checkoutFlatSku({
        sku_key: sku.sku_key,
        property_id: inferredPropertyId,
        success_url: `${window.location.origin}${window.location.pathname}?purchase=complete`,
        cancel_url: window.location.href,
      });
      window.location.href = session.checkout_url;
    } catch {
      toast.error('Could not start checkout. Please try again.');
      setBuyingId(null);
    }
  };

  const decisionPrompt = 'Choose a package to continue with this decision action.';

  return (
    <FmModal open={open} onCancel={onClose} footer={null} title="Unlock this decision">
      <FmStack gap="md">
        <FmBanner tone="warning" title={decisionPrompt} />

        {skus.length > 0 ? (
          <SkusSection skus={skus} buyingId={buyingId} loadingSkus={loadingSkus} onBuy={handleBuy} />
        ) : (
          <>
            <FmText as="p" tone="muted">
              Purchase options are not available here yet. You can continue from settings.
            </FmText>
            <FmButton variant="primary" onClick={() => { onClose(); navigate(appRoutePatterns.settings); }}>
              Open settings
            </FmButton>
          </>
        )}
      </FmStack>
    </FmModal>
  );
};

export default CreditTopUpModal;
